import dayjs from 'dayjs';
import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isEqual } from 'lodash';
import { updateEventById, nextTab } from '../../../../../../app/store/slices/event/event';
import { errorToast } from '../../../../../../utils/toasts/toasts';
import { formatSlotDate } from '../../../../../../utils/dateFormatter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);

// This function now generates the full Yup validation schema
function generateValidationSchema() {
    return Yup.object().shape({
        dates: Yup.array()
            .of(Yup.string().required('Date selection is required'))
            .min(2, 'At least two dates selection are required'),
        schedules: Yup.array().of(
            Yup.object().shape({
                date: Yup.string().required(),
                startTime: Yup.string().required('Start time is required'),
                endTime: Yup.string()
                    .required('End time is required')
                    .test('min_end_time', 'End time must be after start time', function (value) {
                        const { startTime } = this.parent;
                        if (!startTime || !value) return true; // Don't validate if either is missing
                        return dayjs(value).isAfter(dayjs(startTime));
                    }),
                slots: Yup.array().of(
                    Yup.object().shape({
                        startTime: Yup.string().required('Slot start time is required.'),
                        endTime: Yup.string().required('Slot end time is required.')
                    })
                )
            })
        )
    });
}

// const initialValues = {
//     dates: ["19/02/2026", "20/02/2026"],
//     schedules: [
//         {
//             date: "19/02/2026",
//             startTime: dayjs("2025-07-10T10:00"),
//             endTime: dayjs("2025-07-10T12:00"),
//             slots: [
//                 { startTime: dayjs("2025-07-10T10:00"), endTime: dayjs("2025-07-10T10:30") },
//                 { startTime: dayjs("2025-07-10T10:30"), endTime: dayjs("2025-07-10T11:00") }
//             ]
//         },
//         {
//             date: "20/02/2026",
//             startTime: dayjs("2025-07-11T10:00"),
//             endTime: dayjs("2025-07-11T12:00"),
//             slots: []
//         }
//     ]
// };

export default function useMultipleDateEvent() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { eventData } = useSelector((state) => state.event);
    const [isDatePickerClosed, setIsDatePickerClosed] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [slotDurations, setSlotDurations] = useState({});
    const [customSlotDurations, setCustomSlotDurations] = useState({});
    const [timeUnits, setTimeUnits] = useState({});
    const [durationErrors, setDurationErrors] = useState({});
    const [minutes, setMinutes] = useState({});

    const [loading, setLoading] = useState(false)

    function hasMultiDateScheduleChange(values, eventData) {
        const cleanedValues = {
            dates: values.dates.map((date) => dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD')),
            schedules: values.schedules.map((schedule) => ({
                date: dayjs(schedule.date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                startTime: dayjs(schedule.startTime, 'HH:mm').format('HH:mm:ss'),
                endTime: dayjs(schedule.endTime, 'HH:mm').format('HH:mm:ss'),
                slots: schedule.slots.map((slot) => ({
                    startTime: dayjs(slot.startTime, 'HH:mm').format('HH:mm:ss'),
                    endTime: dayjs(slot.endTime, 'HH:mm').format('HH:mm:ss')
                }))
            }))
        };

        const originalData = {
            dates:
                eventData?.schedules?.map((schedule) =>
                    dayjs(schedule.date).format('YYYY-MM-DD')
                ) || [],
            schedules:
                eventData?.schedules?.map((schedule) => ({
                    date: dayjs(schedule.date).format('YYYY-MM-DD'),
                    startTime: dayjs(schedule.from_time, 'HH:mm').format('HH:mm:ss'),
                    endTime: dayjs(schedule.to_time, 'HH:mm').format('HH:mm:ss'),
                    slots: schedule.slots
                        ? schedule.slots.map((slot) => ({
                              startTime: dayjs(slot.from_time, 'HH:mm').format('HH:mm:ss'),
                              endTime: dayjs(slot.to_time, 'HH:mm').format('HH:mm:ss')
                          }))
                        : []
                })) || []
        };

        return !isEqual(cleanedValues, originalData);
    }

    const initialValues =
        eventData?.event_type === 'multi_day'
            ? {
                  dates:
                      eventData?.schedules?.map((schedule) =>
                          dayjs(schedule.date).format('DD/MM/YYYY')
                      ) || [],
                  schedules:
                      eventData?.schedules?.map((schedule) => ({
                          date: dayjs(schedule.date).format('DD/MM/YYYY'),
                          startTime: dayjs(`2000-01-01T${schedule.from_time}`),
                          endTime: dayjs(`2000-01-01T${schedule.to_time}`),
                          slots: schedule.slots
                              ? schedule.slots.map((slot) => ({
                                    startTime: dayjs(`2000-01-01T${slot.from_time}`),
                                    endTime: dayjs(`2000-01-01T${slot.to_time}`)
                                }))
                              : []
                      })) || []
              }
            : {
                  dates: [],
                  schedules: []
              };

    useEffect(() => {
        // If there are schedules in eventData, set isDatePickerClosed to true
        if (eventData?.schedules?.length > 0) {
            setIsDatePickerClosed(true);
        }
    }, [eventData]);

    const multiDateEventFormik = useFormik({
        initialValues,
        validationSchema: generateValidationSchema(),
        validate: (values) => {
            const errors = {};

            values.schedules.forEach((schedule, sIdx) => {
                const scheduleStart = dayjs(schedule.startTime, 'HH:mm');
                const scheduleEnd = dayjs(schedule.endTime, 'HH:mm');

                // 🔹 Validate parent startTime: should not be in the past
                const selectedDate = dayjs(schedule.date, 'DD/MM/YYYY');
                const combinedStartDateTime = dayjs(
                    `${schedule.date} ${schedule.startTime}`,
                    'DD/MM/YYYY HH:mm'
                );
                const now = dayjs();

                if (selectedDate.isSame(now, 'day')) {
                    if (combinedStartDateTime.isBefore(now)) {
                        if (!errors.schedules) errors.schedules = [];
                        if (!errors.schedules[sIdx]) errors.schedules[sIdx] = {};
                        errors.schedules[sIdx].startTime = 'Start time cannot be in the past';
                    }
                } else if (selectedDate.isBefore(now, 'day')) {
                    if (!errors.schedules) errors.schedules = [];
                    if (!errors.schedules[sIdx]) errors.schedules[sIdx] = {};
                    errors.schedules[sIdx].startTime = 'Start time cannot be in the past';
                }

                schedule.slots.forEach((slot, slIdx) => {
                    const slotStart = dayjs(slot.startTime, 'HH:mm');
                    const slotEnd = dayjs(slot.endTime, 'HH:mm');

                    const slotErrors = {};

                    // 🔹 Logic 1: First slot's startTime must match event startTime
                    if (slIdx === 0 && !slotStart.isSame(scheduleStart)) {
                        slotErrors.startTime = 'First slot start time must match event start time';
                    }

                    // 🔹 Logic 2: Slot endTime must be after slot startTime
                    if (!slotEnd.isAfter(slotStart)) {
                        slotErrors.endTime = 'End time cannot be before start time';
                    }

                    // 🔹 Logic 3: Slot endTime must be <= schedule endTime
                    if (slotEnd.isAfter(scheduleEnd)) {
                        slotErrors.endTime = 'End time cannot be after event end time';
                    }

                    // 🔹 Previous slot overlap
                    if (slIdx > 0) {
                        const prevSlot = schedule.slots[slIdx - 1];
                        const prevEnd = dayjs(prevSlot.endTime, 'HH:mm');

                        if (slotStart.isBefore(prevEnd)) {
                            if (!errors.schedules) errors.schedules = [];
                            if (!errors.schedules[sIdx]) errors.schedules[sIdx] = { slots: [] };
                            if (!errors.schedules[sIdx].slots) errors.schedules[sIdx].slots = [];
                            if (!errors.schedules[sIdx].slots[slIdx])
                                errors.schedules[sIdx].slots[slIdx] = {};
                            errors.schedules[sIdx].slots[slIdx].startTime =
                                'Selected time is overlapping with previous slot';
                        }
                    }

                    // 🔁 Check next slots for overlap
                    for (let i = slIdx + 1; i < schedule.slots.length; i++) {
                        const nextSlot = schedule.slots[i];
                        const nextStart = dayjs(nextSlot.startTime, 'HH:mm');
                        const nextEnd = dayjs(nextSlot.endTime, 'HH:mm');

                        if (nextStart.isBefore(slotEnd)) {
                            if (!errors.schedules) errors.schedules = [];
                            if (!errors.schedules[sIdx]) errors.schedules[sIdx] = { slots: [] };
                            if (!errors.schedules[sIdx].slots) errors.schedules[sIdx].slots = [];
                            if (!errors.schedules[sIdx].slots[i])
                                errors.schedules[sIdx].slots[i] = {};
                            errors.schedules[sIdx].slots[i].startTime =
                                'Selected time is overlapping with previous slot';
                        }

                        if (nextEnd.isSameOrBefore(slotEnd)) {
                            if (!errors.schedules) errors.schedules = [];
                            if (!errors.schedules[sIdx]) errors.schedules[sIdx] = { slots: [] };
                            if (!errors.schedules[sIdx].slots) errors.schedules[sIdx].slots = [];
                            if (!errors.schedules[sIdx].slots[i])
                                errors.schedules[sIdx].slots[i] = {};
                            errors.schedules[sIdx].slots[i].endTime =
                                'Selected time is overlapping with previous slot';
                        }

                        // Early exit if nextStart is safely after current slotEnd
                        if (slotEnd.isBefore(nextStart)) {
                            break;
                        }
                    }

                    // ✅ Attach if any errors found
                    if (Object.keys(slotErrors).length > 0) {
                        if (!errors.schedules) errors.schedules = [];
                        if (!errors.schedules[sIdx]) errors.schedules[sIdx] = { slots: [] };
                        if (!errors.schedules[sIdx].slots) errors.schedules[sIdx].slots = [];
                        errors.schedules[sIdx].slots[slIdx] = slotErrors;
                    }
                });
            });

            return errors;
        },
        onSubmit: async (values) => {
            try {

                setLoading(true)

                let hasOneSlotError = false;

                values.schedules.forEach((schedule) => {
                    if (schedule.slots.length === 1) {
                        hasOneSlotError = true;
                        errorToast(
                            `Minimum 2 slots are required for ${formatSlotDate(schedule.date)}`
                        );
                    }
                });

                if (hasOneSlotError) {
                    return false; // prevent submission
                }

                const sortedDates = [...values.dates].sort((a, b) =>
                    dayjs(a, 'DD/MM/YYYY').diff(dayjs(b, 'DD/MM/YYYY'))
                );
                const startDate = sortedDates[0];
                const endDate = sortedDates[sortedDates.length - 1];

                const startSchedule = values.schedules.find((s) => s.date === startDate);
                const endSchedule = values.schedules.find((s) => s.date === endDate);

                const payload = {
                    step: 'schedule',
                    user_id: user?.id,
                    event_type: 'multi_day',
                    start_date: dayjs(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                    end_date: dayjs(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                    start_time: dayjs(startSchedule.startTime).format('HH:mm'),
                    end_time: dayjs(endSchedule.endTime).format('HH:mm'),
                    schedules: values.schedules.map((schedule) => ({
                        date: dayjs(schedule.date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                        from_time: dayjs(schedule.startTime).format('HH:mm:ss'),
                        to_time: dayjs(schedule.endTime).format('HH:mm:ss'),
                        slots:
                            schedule.slots && schedule.slots.length > 0
                                ? schedule.slots.map((slot) => ({
                                      from_time: dayjs(slot.startTime).format('HH:mm:ss'),
                                      to_time: dayjs(slot.endTime).format('HH:mm:ss')
                                  }))
                                : []
                    }))
                };

                // console.log("Payload:", payload);

                if (hasMultiDateScheduleChange(values, eventData)) {
                    await dispatch(
                        updateEventById({
                            eventId: eventData?.eventId,
                            eventData: payload
                        })
                    ).unwrap();
                }

                dispatch(nextTab());
            } catch (error) {
                errorToast(error?.message || 'Failed to update event schedules');
            } finally {
                setLoading(false)
            }
        }
    });

    // console.log("Errors -> ", multiDateEventFormik.errors)

    useEffect(() => {
        const dates = multiDateEventFormik.values.dates;
        const currentSchedules = multiDateEventFormik.values.schedules;

        const scheduleMap = new Map(currentSchedules.map((s) => [s.date, s]));

        const newSchedules = dates.map((date) => {
            if (scheduleMap.has(date)) {
                return scheduleMap.get(date);
            }

            return {
                date,
                startTime: '',
                endTime: '',
                slots: []
            };
        });

        multiDateEventFormik.setFieldValue('schedules', newSchedules, false);

        const buildNewState = (oldState, defaultValue) => {
            const newState = {};
            dates.forEach((date) => {
                newState[date] = oldState.hasOwnProperty(date) ? oldState[date] : defaultValue;
            });
            return newState;
        };

        setExpanded((prev) => buildNewState(prev, false));
        setSlotDurations((prev) => buildNewState(prev, 30));
        setCustomSlotDurations((prev) => buildNewState(prev, ''));
        setTimeUnits((prev) => buildNewState(prev, 'minutes'));
        setMinutes((prev) => buildNewState(prev, 30));
        setDurationErrors((prev) => buildNewState(prev, false));
    }, [multiDateEventFormik.values.dates]);

    const toggleAccordion = useCallback((date) => {
        setExpanded((prev) => ({ ...prev, [date]: !prev[date] }));
    }, []);

    function handleDateSelection(date) {
        const formattedDate = date.format('DD/MM/YYYY');
        const currentDates = multiDateEventFormik.values.dates;
        const newDates = currentDates.includes(formattedDate)
            ? currentDates.filter((d) => d !== formattedDate)
            : [...currentDates, formattedDate].sort(
                  (a, b) => dayjs(a, 'DD/MM/YYYY').valueOf() - dayjs(b, 'DD/MM/YYYY').valueOf()
              );

        multiDateEventFormik.setFieldValue('dates', newDates);
    }

    function handleCloseDatePicker() {
        setIsDatePickerClosed(true);
    }

    function handleDateDeletion(date) {
        const newDates = multiDateEventFormik.values.dates.filter((d) => d !== date);
        multiDateEventFormik.setFieldValue('dates', newDates);
    }

    const handleSlotDurationChange = useCallback((date, value) => {
        setSlotDurations((prev) => ({ ...prev, [date]: value }));
        setMinutes((prev) => ({ ...prev, [date]: parseInt(value, 10) }));
        setCustomSlotDurations((prev) => ({ ...prev, [date]: '' }));
        setDurationErrors((prev) => ({ ...prev, [date]: false }));
    }, []);

    const handleCustomDurationChange = useCallback(
        (date, value) => {
            setCustomSlotDurations((prev) => ({ ...prev, [date]: value }));
            setSlotDurations((prev) => ({ ...prev, [date]: '' }));

            const unit = timeUnits[date] || 'minutes';
            let durationInMinutes =
                unit === 'hour' ? parseInt(value, 10) * 60 : parseInt(value, 10);
            durationInMinutes = isNaN(durationInMinutes) ? 0 : durationInMinutes;

            if (value && durationInMinutes < 5) {
                setDurationErrors((prev) => ({ ...prev, [date]: true }));
            } else {
                setDurationErrors((prev) => ({ ...prev, [date]: false }));
            }

            setMinutes((prev) => ({ ...prev, [date]: durationInMinutes }));
        },
        [timeUnits]
    );

    const addSlotForDate = useCallback(
        (date) => {
            const { schedules } = multiDateEventFormik.values;
            const scheduleIndex = schedules.findIndex((s) => s.date === date);

            if (scheduleIndex === -1) return;

            const schedule = schedules[scheduleIndex];
            const { startTime, endTime, slots } = schedule;
            const duration = minutes[date];

            if (!startTime || !endTime) {
                errorToast('Select start time & end time.');
                return;
            }

            if (!duration || duration < 5) {
                errorToast('Modify slot durations, To add more slots.');
                if (!expanded[date]) {
                    toggleAccordion(date);
                }
                return;
            }

            let newStartTime;

            if (slots.length === 0) {
                newStartTime = dayjs(startTime);
            } else {
                const lastSlot = slots[slots.length - 1];
                newStartTime = dayjs(lastSlot.endTime);
            }

            const newEndTime = newStartTime.add(duration, 'minute');

            if (newEndTime.isAfter(dayjs(endTime))) {
                errorToast('Modify slot durations, To add more slots.');
                return;
            }

            const newSlot = {
                startTime: newStartTime,
                endTime: newEndTime
            };
            const updatedSlots = [...slots, newSlot];
            multiDateEventFormik.setFieldValue(`schedules[${scheduleIndex}].slots`, updatedSlots);

            if (!expanded[date]) {
                toggleAccordion(date);
            }
        },
        [multiDateEventFormik, minutes, expanded, toggleAccordion]
    );

    const deleteSlotForDate = useCallback(
        (date, slotIndexToDelete) => {
            const { schedules } = multiDateEventFormik.values;
            const scheduleIndex = schedules.findIndex((s) => s.date === date);
            if (scheduleIndex === -1) return;

            const updatedSlots = schedules[scheduleIndex].slots.filter(
                (_, index) => index !== slotIndexToDelete
            );
            multiDateEventFormik.setFieldValue(`schedules[${scheduleIndex}].slots`, updatedSlots);
        },
        [multiDateEventFormik]
    );

    // console.log("Multi Date Event Formik Values:", multiDateEventFormik.values);

    return {
        multiDateEventFormik,
        handleDateSelection,
        handleDateDeletion,
        handleCloseDatePicker,
        isDatePickerClosed,
        expanded,
        toggleAccordion,
        addSlotForDate,
        deleteSlotForDate,
        handleSlotDurationChange,
        slotDurations,
        handleCustomDurationChange,
        customSlotDurations,
        timeUnits,
        setTimeUnits,
        durationErrors,
        loading
    };
}

/**
 * 1. start time should be same as event start time
 * 2. add selected time overlapping validation
 * 3. End time cannot be after event end time
 */
